import { RecordTable } from '../record/record-table';
import { IfWrites } from '../write-access';
import { Link } from 'react-router-dom';
import type { SubjectListEntry, SubjectStatus } from '@ni/shared';
import { KNOWN_LEVEL_LABEL, latestPhrase } from '../../lib/copy';
import { StatusWord } from '../ui/status-word';
import { formatDay } from '../../lib/format';
import { CANCELLED_BY_OFFICE, IDENTITY_WITHDRAWN } from '../../lib/run-state';
import { TrackDoor } from './track-door';
import { LinkRow } from '../ui/row-link';
import './people-table.css';

/**
 * **The People table**, in the demo's plain `.table` grammar: Name, Status,
 * Latest, Updated. Home shows a short recent slice of it and `/people` shows all
 * of it, and they are the same component because they are the same rows — the
 * moment they were two tables they would drift.
 *
 * What is new is that a row is not necessarily a subject (import note §1.3). A
 * **known** person is an entity the org holds with no record of their own: the
 * spouse research minted, and every person a file kept without tracking. They
 * have no page to link to, no status, and no runs, so:
 *
 * - the name is text rather than a link — a link to a page that does not exist
 *   is worse than no link;
 * - the status cell says "Known", which is what stands in for the status they
 *   do not have;
 * - the Latest cell is empty, because nothing has ever been run about them.
 *
 * The one thing the row *can* do is the adopt door: choosing a word gives them
 * a subject record and carries the entity — every fact already on it — across.
 * That is offered here rather than on a page of their own, because here is
 * where they are found.
 */

/** Everything the row's adopt door needs, owned by the route that writes. */
export interface PromoteState {
  /** The vertical whose words the picker offers — the reader's own org's. */
  vertical: string;
  /** The entity whose picker is open, if any. */
  openFor: string | null;
  /** The entity whose promotion is in flight. */
  busyFor: string | null;
  /** A failed promotion, reported under the row that asked for it. */
  error: unknown;
  onOpen: (entityId: string | null) => void;
  onChoose: (entry: SubjectListEntry, status: SubjectStatus) => void;
}

export function PeopleTable({
  entries,
  promote,
  account = false,
}: {
  entries: readonly SubjectListEntry[];
  /** Absent on Home, whose rows are tracked people with nothing to promote. */
  promote?: PromoteState | null;
  /**
   * **The protection account column**, in a security org and nowhere else
   * (security fold, 2026-09-22). Every row carries the field, and in every
   * other vertical it is `null` on every row — a column of blanks is furniture,
   * which is the same call the organisations table makes about Status.
   */
  account?: boolean;
}) {
  return (
    <RecordTable
      className={account ? 'people-table with-account' : 'people-table'}
      headings={
        account
          ? ['Name', 'Protection account', 'Status', 'Latest', 'Updated']
          : ['Name', 'Status', 'Latest', 'Updated']
      }
    >
      {entries.map((entry) => (
        <PersonRow
          key={entry.entity_id}
          entry={entry}
          promote={promote ?? null}
          account={account}
        />
      ))}
    </RecordTable>
  );
}

function PersonRow({
  entry,
  promote,
  account,
}: {
  entry: SubjectListEntry;
  promote: PromoteState | null;
  account: boolean;
}) {
  /* Both halves of the test, not just the marker: `id` is what the link needs,
     and a row that says `tracked` without one is a row this build cannot open. */
  const subjectId = entry.level === 'tracked' ? entry.id : null;

  return (
    <LinkRow to={subjectId !== null ? `/people/${subjectId}` : null}>
      <td className="name">
        {subjectId !== null ? (
          <Link className="person-name" to={`/people/${subjectId}`}>
            {entry.name}
          </Link>
        ) : (
          <span className="known-name">{entry.name}</span>
        )}
      </td>
      {account ? (
        <td className="quiet-cell account">
          {entry.account ? (
            <Link className="link" to={`/accounts/${entry.account.id}`}>
              {entry.account.label}
            </Link>
          ) : null}
        </td>
      ) : null}
      <td className="quiet-cell status">
        {subjectId !== null ? (
          entry.status !== null ? (
            <StatusWord vertical={entry.vertical} status={entry.status} />
          ) : null
        ) : (
          <KnownCell entry={entry} promote={promote} />
        )}
      </td>
      {/* Nothing has ever run about a known person, so there is nothing to say
          here — and "Added" would be a claim about a record they do not have. */}
      <td className={`quiet-cell latest${latestTone(entry)}`}>
        {subjectId !== null ? latestPhrase(entry) : null}
      </td>
      <td className="quiet-cell updated">{formatDay(entry.updated_at)}</td>
    </LinkRow>
  );
}

/**
 * A run waiting on the office reads in the body ink, and a failure in copper.
 * A run the office stopped, or one the wrong-person unwind ended, is neither.
 */
function latestTone(entry: SubjectListEntry): string {
  const run = entry.latest_run;
  if (!run) return '';
  if (run.state === 'failed') {
    const settled = run.error_code === IDENTITY_WITHDRAWN || run.error_code === CANCELLED_BY_OFFICE;
    return settled ? '' : ' failed';
  }
  return run.state.startsWith('awaiting_') ? ' waiting' : '';
}

/**
 * The known row's status cell: the word that stands in for a status, and the
 * one gesture that turns it into a real one.
 *
 * The gesture itself is {@link TrackDoor}, which is shared with the knowledge
 * base — the other list that shows known people, and the other place the same
 * door has to behave the same way.
 */
function KnownCell({ entry, promote }: { entry: SubjectListEntry; promote: PromoteState | null }) {
  return (
    /* A div rather than a span: the refusal line inside the door is a block, and
       a block inside an inline element is not a thing a browser has to parse the
       way it is written. */
    <div className="known-cell">
      <span className="known-mark">{KNOWN_LEVEL_LABEL}</span>

      {promote ? (
        <IfWrites>
          <TrackDoor
            vertical={promote.vertical}
            name={entry.name}
            entityId={entry.entity_id}
            open={promote.openFor === entry.entity_id}
            busy={promote.busyFor === entry.entity_id}
            error={promote.error}
            onOpen={promote.onOpen}
            onChoose={(word) => promote.onChoose(entry, word)}
          />
        </IfWrites>
      ) : null}
    </div>
  );
}
